/**
 * Nastavitev odstotkov projektov po sredstvu (docs/ARHITEKTURA.md, 15.3).
 *
 * Seznam sredstev iz registra izbranega meseca in razdelitev na projekte, ki
 * v tem mesecu velja. Bere vsak uporabnik podjetja; spreminja admin ali
 * urednik (`smemUrejati` pove streznik). Sprememba velja od izbranega meseca
 * naprej; pretekli meseci ostanejo, kot so bili. Delez, ki do 100 % manjka,
 * je nerazporejen.
 */
import type { PregledOdstotkov, RazdelitevSredstva, SredstvoZOdstotki } from '@koda/shared';
import { useCallback, useEffect, useRef, useState, type FormEvent, type JSX } from 'react';
import { useParams } from 'react-router-dom';

import { api } from '../lib/api';
import { useBesedilaProjektov } from '../lib/besedila-projektov';
import { datumInUra, decimalno, znesek } from '../lib/format';

/** Odstotek brez odvecnih nicel: 33,3333 %, 50 %, 12,5 %. */
export function odstotekZaPrikaz(odstotek: string): string {
  const stevilo = Number(odstotek);
  if (!Number.isFinite(stevilo)) return odstotek;
  let decimalk = 0;
  while (decimalk < 4 && Math.round(stevilo * 10 ** decimalk) / 10 ** decimalk !== stevilo) decimalk += 1;
  return `${decimalno(String(stevilo), decimalk)} %`;
}

/** Odstotek iz slovenskega zapisa (33,3333) v decimalno besedilo za API; prazno = 0. */
export function odstotekZaApi(besedilo: string): string {
  const ociscen = besedilo.replace(/[\s%]/g, '').replace(',', '.');
  if (ociscen === '') return '0';
  if (!/^\d+(\.\d{1,4})?$/.test(ociscen) || Number(ociscen) > 100) {
    throw new Error('Odstotek mora biti število med 0 in 100 z največ štirimi decimalkami, npr. 33,3333.');
  }
  return ociscen;
}

/** Mesec `llll-mm` v slovenski zapis: 09/2026. */
export function mesecZaPrikaz(mesec: string): string {
  const [leto, m] = mesec.split('-');
  return `${m ?? ''}/${leto ?? ''}`;
}

/** Vsota delezev v obrazcu; null, ce kak vnos ni stevilo. */
function vsotaObrazca(vrednosti: Record<string, string>): number | null {
  let vsota = 0;
  for (const v of Object.values(vrednosti)) {
    try {
      vsota += Number(odstotekZaApi(v));
    } catch {
      return null;
    }
  }
  return Math.round(vsota * 10_000) / 10_000;
}

interface Urejanje {
  inventarna: string;
  veljaOd: string;
  /** Projekt -> odstotek, kot ga tipka uporabnik. */
  vrednosti: Record<string, string>;
  /** Vse razdelitve sredstva; iz njih se vrednosti napolnijo za izbrani mesec veljavnosti. */
  razdelitve: RazdelitevSredstva[];
  /**
   * Ali je uporabnik odstotke ze spremenil. Dokler jih ni, zamenjava meseca
   * veljavnosti napolni odstotke tega meseca; potem ostanejo vneseni, sicer
   * bi zamenjava meseca tiho zavrgla njegov vnos.
   */
  spremenjeno: boolean;
}

/**
 * Delezi, ki veljajo v mesecu `mesec` (zadnja razdelitev z `veljaOd <= mesec`),
 * kot vrednosti obrazca. Obrazec mora pokazati razdelitev meseca, ki ga bo
 * sprememba zamenjala, ne meseca, ki je prikazan v tabeli: sicer bi urejanje
 * v septembrskem pogledu z veljavnostjo od novembra prepisalo novembrsko
 * razdelitev s septembrskimi odstotki.
 */
export function vrednostiZaMesec(
  projekti: { id: string }[],
  razdelitve: RazdelitevSredstva[],
  mesec: string,
): Record<string, string> {
  const velja = razdelitve.filter((r) => r.veljaOd <= mesec).sort((a, b) => b.veljaOd.localeCompare(a.veljaOd))[0];
  return Object.fromEntries(
    projekti.map((p) => {
      const d = velja?.delezi.find((x) => x.projekt === p.id);
      return [p.id, d === undefined ? '' : odstotekZaPrikaz(d.odstotek).replace(' %', '')];
    }),
  );
}

/** Mesec po `llll-mm`. */
export function naslednjiMesec(mesec: string): string {
  const [l, m] = mesec.split('-').map(Number);
  return m === 12 ? `${String(l! + 1)}-01` : `${String(l)}-${String(m! + 1).padStart(2, '0')}`;
}

/** Najmanjsi mesec veljavnosti: ne pred zadnjo spremembo sredstva in ne v zakljucenem mesecu (15.6). */
function najmanjsiMesec(zadnjaSprememba: string | null, zakljucenoDo: string | null): string | undefined {
  const meje = [zadnjaSprememba, zakljucenoDo === null ? null : naslednjiMesec(zakljucenoDo)].filter((m): m is string => m !== null).sort();
  return meje.at(-1);
}

/** Prva crka velika (oznake polj iz slovarja so z malo). */
const veliko = (s: string): string => s.charAt(0).toLocaleUpperCase() + s.slice(1);

export function OdstotkiProjektov(): JSX.Element {
  const { id = '' } = useParams();
  const b = useBesedilaProjektov();
  const [pregled, setPregled] = useState<PregledOdstotkov | null>(null);
  const [mesec, setMesec] = useState<string | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [urejanje, setUrejanje] = useState<Urejanje | null>(null);
  const [napakaObrazca, setNapakaObrazca] = useState<string | null>(null);
  const [shranjujem, setShranjujem] = useState(false);
  const [zgodovina, setZgodovina] = useState<{ inventarna: string; naziv: string; razdelitve: RazdelitevSredstva[] } | null>(null);

  // Zastareli odgovori se zavrzejo: prikaze se samo zadnje branje.
  const zadnjeBranje = useRef(0);
  const osvezi = useCallback(
    (zahtevan: string | null): Promise<void> => {
      const branje = ++zadnjeBranje.current;
      return api
        .odstotkiProjektov(id, zahtevan ?? undefined)
        .then((p) => {
          if (branje !== zadnjeBranje.current) return;
          setPregled(p);
          setMesec(p.mesec);
          setNapaka(null);
        })
        .catch((e: unknown) => {
          if (branje === zadnjeBranje.current) setNapaka(e instanceof Error ? e.message : b.napakaBranja);
        });
    },
    [id],
  );

  useEffect(() => {
    void osvezi(null);
  }, [osvezi]);

  const imeProjekta = (projekt: string): string => pregled?.projekti.find((p) => p.id === projekt)?.naziv ?? projekt;

  const uredi = (s: SredstvoZOdstotki): void => {
    if (pregled === null) return;
    const privzeto = [pregled.privzetoVeljaOd, mesec, s.zadnjaSprememba].filter((m): m is string => m !== null).sort().at(-1) ?? '';
    const projektiPregleda = pregled.projekti;
    setNapakaObrazca(null);
    // Delezi se preberejo iz zgodovine za mesec veljavnosti, ne iz prikazanega meseca.
    api
      .zgodovinaOdstotkov(id, s.inventarna)
      .then((razdelitve) => {
        setUrejanje({
          inventarna: s.inventarna,
          veljaOd: privzeto,
          vrednosti: vrednostiZaMesec(projektiPregleda, razdelitve, privzeto),
          razdelitve,
          spremenjeno: false,
        });
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : b.napakaBranja);
      });
  };

  const shrani = (dogodek: FormEvent): void => {
    dogodek.preventDefault();
    if (urejanje === null) return;
    let delezi;
    try {
      delezi = Object.entries(urejanje.vrednosti)
        .map(([projekt, v]) => ({ projekt, odstotek: odstotekZaApi(v) }))
        .filter((d) => Number(d.odstotek) > 0);
    } catch (e) {
      setNapakaObrazca(e instanceof Error ? b.napakaOdstotka : b.napakaOdstotka);
      return;
    }
    setShranjujem(true);
    api
      .shraniOdstotke(id, urejanje.inventarna, { veljaOd: urejanje.veljaOd, delezi })
      .then(() => {
        setUrejanje(null);
        setZgodovina(null);
        return osvezi(mesec);
      })
      .catch((e: unknown) => {
        setNapakaObrazca(e instanceof Error ? e.message : b.napakaBranja);
      })
      .finally(() => {
        setShranjujem(false);
      });
  };

  const pokaziZgodovino = (s: SredstvoZOdstotki): void => {
    if (zgodovina?.inventarna === s.inventarna) {
      setZgodovina(null);
      return;
    }
    api
      .zgodovinaOdstotkov(id, s.inventarna)
      .then((razdelitve) => {
        setZgodovina({ inventarna: s.inventarna, naziv: s.naziv, razdelitve });
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : b.napakaBranja);
      });
  };

  const odstraniZadnjo = (inventarna: string, veljaOd: string): void => {
    if (!window.confirm(b.potrdiOdstranitev(mesecZaPrikaz(veljaOd)))) return;
    api
      .odstraniOdstotke(id, inventarna, veljaOd)
      .then(() => {
        setZgodovina(null);
        return osvezi(mesec);
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : b.napakaBranja);
      });
  };

  const vsota = urejanje === null ? null : vsotaObrazca(urejanje.vrednosti);
  const projekti = pregled?.projekti ?? [];

  return (
    <>
      {pregled !== null && <p className="podnaslov">{b.odstotkiUvod(pregled.najvecProjektovNaSredstvo)}</p>}
      {pregled?.zakljucenoDo != null && (
        <p className="drobno">{b.zakljucenoDo(mesecZaPrikaz(pregled.zakljucenoDo), mesecZaPrikaz(naslednjiMesec(pregled.zakljucenoDo)))}</p>
      )}

      <section className="kartica">
        <label>
          {veliko(b.mesec)}{' '}
          <input
            type="month"
            aria-label={veliko(b.mesec)}
            value={mesec ?? ''}
            onChange={(d) => {
              if (d.target.value === '') return;
              setUrejanje(null);
              setZgodovina(null);
              void osvezi(d.target.value);
            }}
          />
        </label>
        {pregled?.register != null && (
          <span className="drobno">
            {' '}
            {b.sredstvaIzRegistra(mesecZaPrikaz(`${String(pregled.register.leto)}-${String(pregled.register.mesec).padStart(2, '0')}`))}
          </span>
        )}
      </section>

      {napaka !== null && (
        <p className="napaka" role="alert">
          {napaka}
        </p>
      )}
      {pregled !== null && pregled.register === null && (
        <p className="drobno">{b.niRegistra}</p>
      )}
      {pregled !== null && pregled.register !== null && projekti.length === 0 && (
        <p className="drobno">{b.niOdprtihProjektov}</p>
      )}

      {pregled !== null && pregled.sredstva.length > 0 && (
        <form onSubmit={shrani}>
          <div className="tabela-ovoj">
            <table>
              <thead>
                <tr>
                  <th>{b.invSt}</th>
                  <th>{b.naziv}</th>
                  <th className="stevilka">{b.neodpisana}</th>
                  <th className="stevilka">{b.mesecnaAmortizacija}</th>
                  {projekti.map((p) => (
                    <th key={p.id} className="stevilka">
                      {p.naziv}
                      {!p.aktiven && <span className="drobno"> ({b.zakljucen})</span>}
                    </th>
                  ))}
                  <th className="stevilka">{b.nerazporejeno}</th>
                  <th>{b.veljaOd}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pregled.sredstva.map((s) => {
                  const ureja = urejanje?.inventarna === s.inventarna;
                  return (
                    <tr key={s.inventarna}>
                      <td>
                        <code>{s.inventarna}</code>
                      </td>
                      <td>{s.naziv}</td>
                      <td className="stevilka">{znesek(s.neodpisanaVrednost)}</td>
                      <td className="stevilka">{s.mesecnaAmortizacija === null ? '—' : znesek(s.mesecnaAmortizacija)}</td>
                      {projekti.map((p) => {
                        if (ureja && urejanje !== null) {
                          return (
                            <td key={p.id} className="stevilka">
                              <input
                                type="text"
                                inputMode="decimal"
                                size={7}
                                aria-label={b.odstotekProjekta(p.naziv)}
                                value={urejanje.vrednosti[p.id] ?? ''}
                                onChange={(d) => {
                                  setUrejanje({ ...urejanje, spremenjeno: true, vrednosti: { ...urejanje.vrednosti, [p.id]: d.target.value } });
                                }}
                              />
                            </td>
                          );
                        }
                        const d = s.velja?.delezi.find((x) => x.projekt === p.id);
                        return (
                          <td key={p.id} className="stevilka">
                            {d === undefined ? '—' : odstotekZaPrikaz(d.odstotek)}
                          </td>
                        );
                      })}
                      <td className="stevilka">
                        {ureja
                          ? vsota === null
                            ? '?'
                            : odstotekZaPrikaz(String(Math.round((100 - vsota) * 10_000) / 10_000))
                          : odstotekZaPrikaz(s.velja?.nerazporejeno ?? '100')}
                      </td>
                      <td>
                        {ureja && urejanje !== null ? (
                          <input
                            type="month"
                            aria-label={veliko(b.veljaOd)}
                            value={urejanje.veljaOd}
                            min={najmanjsiMesec(s.zadnjaSprememba, pregled.zakljucenoDo)}
                            onChange={(d) => {
                              // Nov mesec veljavnosti: dokler odstotki niso spremenjeni,
                              // se napolnijo iz razdelitve, ki takrat velja; vneseni ostanejo.
                              const napolni = !urejanje.spremenjeno && d.target.value !== '';
                              setUrejanje({
                                ...urejanje,
                                veljaOd: d.target.value,
                                vrednosti: napolni ? vrednostiZaMesec(projekti, urejanje.razdelitve, d.target.value) : urejanje.vrednosti,
                              });
                            }}
                            required
                          />
                        ) : s.velja === null ? (
                          '—'
                        ) : (
                          mesecZaPrikaz(s.velja.veljaOd)
                        )}
                        {!ureja && s.zadnjaSprememba !== null && s.zadnjaSprememba !== s.velja?.veljaOd && (
                          <span className="drobno"> {b.spremembaOd(mesecZaPrikaz(s.zadnjaSprememba))}</span>
                        )}
                      </td>
                      <td>
                        {ureja ? (
                          <>
                            <button type="submit" disabled={shranjujem || vsota === null || vsota > 100}>
                              {b.shrani}
                            </button>{' '}
                            <button
                              type="button"
                              className="gumb-besedilo"
                              onClick={() => {
                                setUrejanje(null);
                                setNapakaObrazca(null);
                              }}
                            >
                              {b.preklici}
                            </button>
                          </>
                        ) : (
                          <>
                            {pregled.smemUrejati && urejanje === null && projekti.length > 0 && (
                              <>
                                <button
                                  type="button"
                                  className="gumb-besedilo"
                                  aria-label={`${veliko(b.uredi)} ${s.inventarna}`}
                                  onClick={() => {
                                    uredi(s);
                                  }}
                                >
                                  {b.uredi}
                                </button>
                                {' · '}
                              </>
                            )}
                            <button
                              type="button"
                              className="gumb-besedilo"
                              aria-label={`${veliko(b.zgodovina)} ${s.inventarna}`}
                              onClick={() => {
                                pokaziZgodovino(s);
                              }}
                            >
                              {b.zgodovina}
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {urejanje !== null && vsota !== null && vsota > 100 && (
            <p className="napaka" role="alert">
              {b.vsotaNad100(odstotekZaPrikaz(String(vsota)))}
            </p>
          )}
          {napakaObrazca !== null && (
            <p className="napaka" role="alert">
              {napakaObrazca}
            </p>
          )}
        </form>
      )}

      {zgodovina !== null && (
        <section className="kartica" aria-label={b.zgodovinaNaslov}>
          <h2>
            {b.zgodovinaNaslov}: <code>{zgodovina.inventarna}</code> {zgodovina.naziv}
          </h2>
          {zgodovina.razdelitve.length === 0 ? (
            <p className="drobno">{b.brezRazdelitve}</p>
          ) : (
            <ul>
              {zgodovina.razdelitve.map((r, i) => (
                <li key={r.veljaOd}>
                  {b.od} <strong>{mesecZaPrikaz(r.veljaOd)}</strong>:{' '}
                  {r.delezi.length === 0
                    ? b.vseNerazporejeno
                    : r.delezi.map((d) => `${imeProjekta(d.projekt)} ${odstotekZaPrikaz(d.odstotek)}`).join(', ')}
                  {r.delezi.length > 0 && `, ${b.nerazporejeno} ${odstotekZaPrikaz(r.nerazporejeno)}`}
                  <span className="drobno">
                    {' '}
                    ({r.vnesel ?? b.neznan}, {datumInUra(r.vneseno)})
                  </span>
                  {i === 0 && pregled?.smemUrejati === true && (pregled.zakljucenoDo === null || r.veljaOd > pregled.zakljucenoDo) && (
                    <>
                      {' '}
                      <button
                        type="button"
                        className="gumb-besedilo"
                        onClick={() => {
                          odstraniZadnjo(zgodovina.inventarna, r.veljaOd);
                        }}
                      >
                        {b.odstrani}
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </>
  );
}
